import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, computed, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { TranslateModule } from '@ngx-translate/core';
import { MarketingProfileStoreService } from '../../../services/marketing-profile/marketing-profile.store.service';

/**
 * One member, one sheet. Every figure is placed in context rather than shown
 * alone, and the marketing signals at the top are rules over the data of the
 * moment: nothing here waits for a deploy to move.
 */
@Component({
  selector: 'bo-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  imports: [CommonModule, TranslateModule, GoogleMap, MapMarker],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileComponent implements OnInit, OnChanges {
  /** When set, the sheet shows this member and ignores the route: that is
   * how the management screen embeds it in a side panel. */
  @Input() userId: string | null = null;

  private readonly store = inject(MarketingProfileStoreService);
  private readonly route = inject(ActivatedRoute);

  readonly profile = this.store.profile;
  readonly loading = this.store.loading;
  readonly failed = this.store.failed;

  readonly membershipMonths = computed(() => {
    const created = this.profile()?.createdAt;
    if (!created) {
      return null;
    }
    return Math.max(Math.floor((Date.now() - new Date(created).getTime()) / (1000 * 60 * 60 * 24 * 30.44)), 0);
  });

  readonly loginDaysAgo = computed(() => {
    const login = this.profile()?.lastLoginAt;
    if (!login) {
      return null;
    }
    return Math.max(Math.floor((Date.now() - new Date(login).getTime()) / (1000 * 60 * 60 * 24)), 0);
  });

  readonly mapOptions = computed<google.maps.MapOptions | null>(() => {
    const position = this.profile()?.position;
    if (!position) {
      return null;
    }
    return {
      center: { lat: position.lat, lng: position.lon },
      zoom: 10,
      mapTypeControl: false,
      streetViewControl: false,
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ],
    };
  });

  readonly markerPosition = computed<google.maps.LatLngLiteral | null>(() => {
    const position = this.profile()?.position;
    return position ? { lat: position.lat, lng: position.lon } : null;
  });

  readonly pushEntries = computed(() => Object.entries(this.profile()?.pushSwitches ?? {}));
  readonly mailEntries = computed(() => Object.entries(this.profile()?.mailSwitches ?? {}));

  ngOnInit(): void {
    if (this.userId) {
      this.store.load(this.userId);
      return;
    }
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.store.load(id);
      }
    });
  }

  ngOnChanges(): void {
    if (this.userId) {
      this.store.load(this.userId);
    }
  }

  initials(): string {
    const profile = this.profile();
    if (!profile) {
      return '?';
    }
    return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase() || '?';
  }

  euros(cents: number): string {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }
}
